import { useEffect, useMemo, useState, type FormEvent } from "react";
import type {
  AdminCatalogProduct,
  Promotion,
  PromotionBenefitPolicy,
  PromotionInput,
  PromotionPlacements,
  PromotionRule,
  PromotionStatus,
  QuantityPositionPromotionRule,
  SimpleDiscountPromotionRule,
  PromotionStep,
  PromotionTier
} from "./types";

const defaultPlacements: PromotionPlacements = {
  announcementBar: true,
  homeHero: false,
  productCardBadge: true,
  productDetail: true,
  cartNudge: true,
  checkoutSummary: true
};

const defaultQuantityRule: QuantityPositionPromotionRule = {
  type: "quantity_position",
  allocation: "highest_price_first",
  tiers: [
    {
      minQuantity: 1,
      maxQuantity: null,
      steps: [
        { position: 1, payPct: 100 },
        { position: 2, payPct: 70 },
        { fromPosition: 3, payPct: 50 }
      ]
    }
  ]
};

const defaultSimpleRule: SimpleDiscountPromotionRule = {
  type: "simple_discount",
  discount: {
    kind: "percentage",
    value: 10,
    application: "per_unit"
  },
  target: { type: "store" }
};

const emptyPromotion: PromotionInput = {
  id: "",
  name: "",
  status: "draft",
  priority: 0,
  benefitPolicy: "best_benefit",
  startsAt: null,
  endsAt: null,
  activation: { type: "automatic" },
  rule: defaultQuantityRule,
  presentation: {
    headline: "Mas llevas, menos pagas",
    shortLabel: "Descuento por cantidad",
    badge: "Hasta 50% menos",
    cartNudge: "Agrega {remainingUnits} mas para desbloquear el siguiente beneficio.",
    activeMessage: "Promocion aplicada. Estas ahorrando {savings}.",
    terms: "Aplica sobre las referencias participantes.",
    showProductDetailPricingTable: true,
    placements: defaultPlacements
  }
};

function normalizeRule(rule: Promotion["rule"] | PromotionInput["rule"]): PromotionRule {
  const legacyRule = rule as { type?: PromotionRule["type"]; tiers?: PromotionTier[]; allocation?: "highest_price_first" };
  if (legacyRule.type) return rule as PromotionRule;

  return {
    type: "quantity_position",
    allocation: legacyRule.allocation || "highest_price_first",
    tiers: legacyRule.tiers || defaultQuantityRule.tiers
  };
}

function stepsToText(steps: PromotionStep[]) {
  return steps
    .map((step) => (
      "position" in step
        ? `${step.position}:${step.payPct}`
        : `${step.fromPosition}+:${step.payPct}`
    ))
    .join(", ");
}

function parseSteps(value: string): PromotionStep[] {
  return value
    .split(",")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = /^(\d+)(\+)?\s*:\s*(\d+)$/.exec(line);
      if (!match) throw new Error(`Paso invalido: "${line}". Usa posicion:porcentaje, por ejemplo 2:70 o 3+:50.`);

      const position = Number(match[1]);
      const payPct = Number(match[3]);
      if (position < 1 || payPct < 0 || payPct > 100) {
        throw new Error(`Paso fuera de rango: "${line}".`);
      }

      return match[2]
        ? { fromPosition: position, payPct }
        : { position, payPct };
    });
}

function tiersToText(tiers: PromotionTier[]) {
  return tiers.map((tier) => {
    const range = tier.maxQuantity === null
      ? `${tier.minQuantity}+`
      : tier.maxQuantity === tier.minQuantity
        ? `${tier.minQuantity}`
        : `${tier.minQuantity}-${tier.maxQuantity}`;
    return `${range} => ${stepsToText(tier.steps)}`;
  }).join("\n");
}

function parseTiers(value: string): PromotionTier[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = /^(\d+)(?:-(\d+)|(\+))?\s*=>\s*(.+)$/.exec(line);
      if (!match) {
        throw new Error(`Nivel invalido: "${line}". Usa por ejemplo 2 => 1:100, 2:50 o 3+ => 1:100, 2+:40.`);
      }

      const minQuantity = Number(match[1]);
      const maxQuantity = match[3]
        ? null
        : match[2]
          ? Number(match[2])
          : minQuantity;

      return {
        minQuantity,
        maxQuantity,
        steps: parseSteps(match[4])
      };
    });
}

function toLocalDateTime(value: string | null) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function toIsoOrNull(value: string) {
  return value ? new Date(value).toISOString() : null;
}

type PromotionTextField = Exclude<keyof PromotionInput["presentation"], "placements" | "showProductDetailPricingTable">;

function productLabel(product: AdminCatalogProduct) {
  return `${product.title} - ${product.slug || product.id}`;
}

export function PromotionEditor({
  products = [],
  promotion,
  onSave,
  onCancel
}: {
  products?: AdminCatalogProduct[];
  promotion: Promotion | null;
  onSave: (input: PromotionInput, isNew: boolean) => Promise<void>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<PromotionInput>(emptyPromotion);
  const [tiersText, setTiersText] = useState(tiersToText(defaultQuantityRule.tiers));
  const [productSearch, setProductSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const next = promotion
      ? {
          id: promotion.id,
          name: promotion.name,
          status: promotion.status,
          priority: promotion.priority,
          benefitPolicy: promotion.benefitPolicy || "best_benefit",
          startsAt: promotion.startsAt,
          endsAt: promotion.endsAt,
          activation: promotion.activation || { type: "automatic" as const },
          rule: normalizeRule(promotion.rule),
          presentation: {
            ...emptyPromotion.presentation,
            ...promotion.presentation,
            showProductDetailPricingTable: promotion.presentation.showProductDetailPricingTable ?? true,
            placements: {
              ...defaultPlacements,
              ...promotion.presentation.placements
            }
          }
        }
      : emptyPromotion;
    setDraft(next);
    setTiersText(next.rule.type === "quantity_position" ? tiersToText(next.rule.tiers) : tiersToText(defaultQuantityRule.tiers));
    setProductSearch("");
    setError(null);
  }, [promotion]);

  const simpleRule = draft.rule.type === "simple_discount" ? draft.rule : defaultSimpleRule;
  const selectedProductIds = simpleRule.target.type === "products"
    ? simpleRule.target.productIds
    : [];
  const filteredProducts = useMemo(() => {
    const query = productSearch.trim().toLowerCase();
    if (!query) return products.slice(0, 20);
    return products
      .filter((product) => productLabel(product).toLowerCase().includes(query))
      .slice(0, 30);
  }, [productSearch, products]);

  function updatePresentation(field: PromotionTextField, value: string) {
    setDraft((current) => ({
      ...current,
      presentation: { ...current.presentation, [field]: value }
    }));
  }

  function toggleProductDetailPricingTable() {
    setDraft((current) => ({
      ...current,
      presentation: {
        ...current.presentation,
        showProductDetailPricingTable: !current.presentation.showProductDetailPricingTable
      }
    }));
  }

  function togglePlacement(field: keyof PromotionPlacements) {
    setDraft((current) => ({
      ...current,
      presentation: {
        ...current.presentation,
        placements: {
          ...current.presentation.placements,
          [field]: !current.presentation.placements[field]
        }
      }
    }));
  }

  function setRuleType(type: PromotionRule["type"]) {
    setDraft((current) => ({
      ...current,
      rule: type === "quantity_position" ? defaultQuantityRule : defaultSimpleRule
    }));
  }

  function updateSimpleRule(rule: SimpleDiscountPromotionRule) {
    if (rule.type !== "simple_discount") return;
    setDraft((current) => ({ ...current, rule }));
  }

  function toggleProduct(productId: string) {
    if (draft.rule.type !== "simple_discount") return;
    const currentIds = simpleRule.target.type === "products" ? simpleRule.target.productIds : [];
    const nextIds = currentIds.includes(productId)
      ? currentIds.filter((id) => id !== productId)
      : [...currentIds, productId];
    updateSimpleRule({
      ...simpleRule,
      target: { type: "products", productIds: nextIds }
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      const rule: PromotionRule = draft.rule.type === "quantity_position"
        ? {
            type: "quantity_position",
            allocation: "highest_price_first",
            tiers: parseTiers(tiersText)
          }
        : draft.rule;
      const input = {
        ...draft,
        startsAt: draft.startsAt ? toIsoOrNull(draft.startsAt) : null,
        endsAt: draft.endsAt ? toIsoOrNull(draft.endsAt) : null,
        activation: draft.activation.type === "code"
          ? { type: "code" as const, code: draft.activation.code.trim().toUpperCase() }
          : { type: "automatic" as const },
        rule
      };
      await onSave(input, !promotion);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "No se pudo guardar la promocion.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form className="promotion-editor" onSubmit={submit}>
      <div className="promotion-editor-heading">
        <div>
          <span>Configuracion comercial</span>
          <h2>{promotion ? `Editar ${promotion.name}` : "Nueva promocion"}</h2>
        </div>
        <button className="ghost-button" type="button" onClick={onCancel}>Cerrar</button>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <div className="form-grid">
        <label>
          ID estable
          <input
            value={draft.id}
            disabled={Boolean(promotion)}
            placeholder="promo-especial"
            pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
            required
            onChange={(event) => setDraft({ ...draft, id: event.target.value })}
          />
        </label>
        <label>
          Nombre interno
          <input value={draft.name} required onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
        </label>
        <label>
          Estado
          <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value as PromotionStatus })}>
            <option value="draft">Borrador</option>
            <option value="active">Activa</option>
            <option value="inactive">Inactiva</option>
            <option value="archived">Archivada</option>
          </select>
        </label>
        <label>
          Prioridad
          <input type="number" value={draft.priority} onChange={(event) => setDraft({ ...draft, priority: Number(event.target.value) })} />
        </label>
        <label>
          Politica embajador
          <select value={draft.benefitPolicy} onChange={(event) => setDraft({ ...draft, benefitPolicy: event.target.value as PromotionBenefitPolicy })}>
            <option value="best_benefit">Mejor beneficio</option>
            <option value="brand_exclusive">Exclusiva de marca</option>
          </select>
          <small>En exclusiva de marca no compite el descuento del embajador.</small>
        </label>
        <label>
          Activacion
          <select
            value={draft.activation.type}
            onChange={(event) => setDraft({
              ...draft,
              activation: event.target.value === "code" ? { type: "code", code: "" } : { type: "automatic" }
            })}
          >
            <option value="automatic">Automatica</option>
            <option value="code">Por codigo</option>
          </select>
        </label>
        {draft.activation.type === "code" ? (
          <label>
            Codigo promocional
            <input
              value={draft.activation.code}
              placeholder="DIALIBRE20"
              required
              onChange={(event) => setDraft({ ...draft, activation: { type: "code", code: event.target.value } })}
            />
          </label>
        ) : null}
        <label>
          Inicia
          <input type="datetime-local" value={toLocalDateTime(draft.startsAt)} onChange={(event) => setDraft({ ...draft, startsAt: event.target.value || null })} />
        </label>
        <label>
          Finaliza
          <input type="datetime-local" value={toLocalDateTime(draft.endsAt)} onChange={(event) => setDraft({ ...draft, endsAt: event.target.value || null })} />
          <small>Vacio significa sin fecha de finalizacion.</small>
        </label>
      </div>

      <div className="form-grid">
        <label>
          Tipo de promocion
          <select value={draft.rule.type} onChange={(event) => setRuleType(event.target.value as PromotionRule["type"])}>
            <option value="quantity_position">Volumen por posicion</option>
            <option value="simple_discount">Descuento simple</option>
          </select>
        </label>
        {draft.rule.type === "simple_discount" ? (
          <>
            <label>
              Valor
              <input
                type="number"
                min="0"
                value={simpleRule.discount.value}
                onChange={(event) => updateSimpleRule({
                  ...simpleRule,
                  discount: { ...simpleRule.discount, value: Number(event.target.value) }
                })}
              />
            </label>
            <label>
              Tipo de valor
              <select value={simpleRule.discount.kind} onChange={(event) => updateSimpleRule({
                ...simpleRule,
                discount: { ...simpleRule.discount, kind: event.target.value as "percentage" | "fixed_amount" }
              })}>
                <option value="percentage">Porcentaje</option>
                <option value="fixed_amount">Monto fijo</option>
              </select>
            </label>
            <label>
              Aplicacion
              <select value={simpleRule.discount.application} onChange={(event) => updateSimpleRule({
                ...simpleRule,
                discount: { ...simpleRule.discount, application: event.target.value as "per_unit" | "subtotal" }
              })}>
                <option value="per_unit">Por unidad elegible</option>
                <option value="subtotal">Sobre subtotal elegible</option>
              </select>
            </label>
            <label>
              Alcance
              <select value={simpleRule.target.type} onChange={(event) => updateSimpleRule({
                ...simpleRule,
                target: event.target.value === "products" ? { type: "products", productIds: [] } : { type: "store" }
              })}>
                <option value="store">Toda la tienda</option>
                <option value="products">Referencias seleccionadas</option>
              </select>
            </label>
          </>
        ) : null}
      </div>

      {draft.rule.type === "quantity_position" ? (
        <label>
          Niveles por cantidad total y porcentaje pagado por posicion
          <textarea rows={5} value={tiersText} onChange={(event) => setTiersText(event.target.value)} placeholder="1+ => 1:100, 2:70, 3+:50" required />
          <small>Una linea por nivel. Ejemplos: 2 =&gt; 1:100, 2:50 y 3+ =&gt; 1:100, 2+:40.</small>
        </label>
      ) : null}

      {draft.rule.type === "simple_discount" && simpleRule.target.type === "products" ? (
        <fieldset>
          <legend>Referencias participantes</legend>
          <span className="content-search-input">
            <input value={productSearch} placeholder="Buscar por nombre, slug o ID" onChange={(event) => setProductSearch(event.target.value)} />
          </span>
          <div className="placement-grid">
            {filteredProducts.map((product) => (
              <label key={product.id} className="check-label">
                <input type="checkbox" checked={selectedProductIds.includes(product.id)} onChange={() => toggleProduct(product.id)} />
                {productLabel(product)}
              </label>
            ))}
          </div>
          <small>{selectedProductIds.length} referencias seleccionadas.</small>
        </fieldset>
      ) : null}

      <div className="form-grid">
        <label>
          Titular
          <input value={draft.presentation.headline} required onChange={(event) => updatePresentation("headline", event.target.value)} />
        </label>
        <label>
          Etiqueta corta
          <input value={draft.presentation.shortLabel} required onChange={(event) => updatePresentation("shortLabel", event.target.value)} />
        </label>
        <label>
          Badge
          <input value={draft.presentation.badge} required onChange={(event) => updatePresentation("badge", event.target.value)} />
        </label>
        <label>
          Mensaje para completar
          <input value={draft.presentation.cartNudge} onChange={(event) => updatePresentation("cartNudge", event.target.value)} />
        </label>
        <label>
          Mensaje aplicado
          <input value={draft.presentation.activeMessage} onChange={(event) => updatePresentation("activeMessage", event.target.value)} />
        </label>
        <label>
          Terminos
          <textarea rows={3} value={draft.presentation.terms} onChange={(event) => updatePresentation("terms", event.target.value)} />
        </label>
      </div>

      <fieldset>
        <legend>Ubicaciones visibles</legend>
        <div className="placement-grid">
          {Object.entries(draft.presentation.placements).map(([field, enabled]) => (
            <label key={field} className="check-label">
              <input type="checkbox" checked={enabled} onChange={() => togglePlacement(field as keyof PromotionPlacements)} />
              {field}
            </label>
          ))}
          {draft.rule.type === "quantity_position" ? (
            <label className="check-label">
              <input type="checkbox" checked={draft.presentation.showProductDetailPricingTable} onChange={toggleProductDetailPricingTable} />
              Tabla de precios en PDP
            </label>
          ) : null}
        </div>
      </fieldset>

      <div className="button-row">
        <button className="primary-button" type="submit" disabled={isSaving}>{isSaving ? "Guardando..." : "Guardar promocion"}</button>
        <button className="ghost-button" type="button" onClick={onCancel}>Cancelar</button>
      </div>
    </form>
  );
}
